import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2,
  Check,
  ChevronsUpDown,
  CreditCard,
  Loader2,
  Plus,
  Settings as SettingsIcon,
  Users,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTenantMe } from "@/lib/tenant-me";
import { useSwitchWorkspace, useWorkspaces } from "@/lib/workspaces";
import { cn } from "@/lib/utils";
import { CreateWorkspaceDialog } from "./CreateWorkspaceDialog";

/**
 * Top-of-sidebar block — the canonical surface for "which workspace
 * am I in" + workspace settings + switching to another workspace
 * the operator belongs to. Mirrors the Slack / Notion / Linear
 * pattern.
 *
 * The trigger is always rendered (even with a single membership)
 * because it's the workspace identity, not just a switcher. The
 * popover hides the empty Workspaces section when there's only one
 * membership but keeps the workspace settings links visible.
 */
export function SidebarWorkspaceMenu() {
  const tenant = useTenantMe();
  const { data, isLoading } = useWorkspaces();
  const switchMutation = useSwitchWorkspace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const t = tenant.data?.tenant;
  const tenantName = t?.name ?? "Workspace";
  const logoUrl = t?.logoUrl ?? null;
  const initial = tenantName.trim().charAt(0).toUpperCase() || "W";

  const workspaces = data?.workspaces ?? [];
  const current = workspaces.find((w) => w.isCurrent);
  const others = workspaces.filter((w) => !w.isCurrent);
  const role = current?.role ?? "";

  const onPick = async (tenantId: string) => {
    if (switchMutation.isPending) return;
    try {
      await switchMutation.mutateAsync(tenantId);
      setOpen(false);
      navigate("/", { replace: true });
    } catch {
      // mutation surfaces the error; nothing else to do here.
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "w-full flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors",
            "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          )}
          aria-label="Switch workspace"
        >
          <WorkspaceLogo logoUrl={logoUrl} initial={initial} />
          <div className="min-w-0 flex-1 text-left">
            <p className="text-sm font-semibold tracking-tight truncate leading-tight">
              {tenantName}
            </p>
            {role && (
              <p className="text-[11px] text-sidebar-muted leading-tight capitalize">
                {role}
              </p>
            )}
          </div>
          <ChevronsUpDown className="h-3.5 w-3.5 text-sidebar-muted shrink-0" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        className="w-72 p-1"
      >
        {/* Other workspaces (omit when only one membership) */}
        {!isLoading && others.length > 0 && (
          <>
            <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Switch workspace
            </p>
            {/* Show the current as the first row, then others */}
            {current && (
              <RowCurrent
                name={current.tenantName}
                role={current.role}
                logoUrl={logoUrl}
                initial={initial}
              />
            )}
            {others.map((w) => (
              <RowSwitchable
                key={w.tenantId}
                name={w.tenantName}
                role={w.role}
                onClick={() => onPick(w.tenantId)}
                pending={
                  switchMutation.isPending &&
                  switchMutation.variables === w.tenantId
                }
              />
            ))}
            <div className="my-1 h-px bg-border" />
          </>
        )}

        {/* Create another workspace — always visible. The same email
            can own multiple workspaces; the public signup flow blocks
            this (Option-C), but inside the dashboard the operator can
            spin up as many as they need. */}
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setCreateOpen(true);
          }}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-sm hover:bg-muted text-left text-sm"
        >
          <span className="h-8 w-8 rounded-md border border-dashed border-border inline-flex items-center justify-center shrink-0 text-muted-foreground">
            <Plus className="h-4 w-4" />
          </span>
          <span className="text-sm font-medium">Create new workspace</span>
        </button>

        <div className="my-1 h-px bg-border" />

        {/* Workspace links — always visible */}
        <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
          {tenantName}
        </p>
        <PopoverLink
          to="/workspace/general"
          icon={SettingsIcon}
          label="Workspace settings"
          onClose={() => setOpen(false)}
        />
        <PopoverLink
          to="/workspace/team"
          icon={Users}
          label="Team members"
          onClose={() => setOpen(false)}
        />
        <PopoverLink
          to="/workspace/billing"
          icon={CreditCard}
          label="Billing & plan"
          onClose={() => setOpen(false)}
        />

        <div className="px-3 pt-2 pb-1.5 text-[10px] text-muted-foreground border-t border-border mt-1">
          booqly · Booking Platform
        </div>
      </PopoverContent>

      <CreateWorkspaceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </Popover>
  );
}

/* ------------------------------------------------------------------ */

function WorkspaceLogo({
  logoUrl,
  initial,
}: {
  logoUrl: string | null;
  initial: string;
}) {
  if (logoUrl) {
    return (
      <span className="h-8 w-8 rounded-md overflow-hidden bg-muted shrink-0">
        <img src={logoUrl} alt="" className="w-full h-full object-cover" />
      </span>
    );
  }
  return (
    <span className="h-8 w-8 rounded-md bg-primary text-primary-foreground inline-flex items-center justify-center text-sm font-semibold shrink-0">
      {initial}
    </span>
  );
}

function RowCurrent({
  name,
  role,
  logoUrl,
  initial,
}: {
  name: string;
  role: string;
  logoUrl: string | null;
  initial: string;
}) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 rounded-sm bg-muted/50">
      <WorkspaceLogo logoUrl={logoUrl} initial={initial} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{name}</p>
        <p className="text-[11px] text-muted-foreground capitalize">
          {role} · current
        </p>
      </div>
      <Check className="h-4 w-4 text-primary shrink-0" />
    </div>
  );
}

function RowSwitchable({
  name,
  role,
  onClick,
  pending,
}: {
  name: string;
  role: string;
  onClick: () => void;
  pending: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-sm hover:bg-muted text-left disabled:opacity-60"
    >
      <span className="h-8 w-8 rounded-md bg-muted text-muted-foreground inline-flex items-center justify-center text-sm font-semibold shrink-0">
        <Building2 className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{name}</p>
        <p className="text-[11px] text-muted-foreground capitalize">{role}</p>
      </div>
      {pending ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
      ) : (
        <span className="text-[11px] text-primary font-medium shrink-0">
          Switch
        </span>
      )}
    </button>
  );
}

function PopoverLink({
  to,
  icon: Icon,
  label,
  onClose,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClose: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-sm hover:bg-muted text-sm"
    >
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <span>{label}</span>
    </Link>
  );
}
